import { useCallback, useContext, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { Plus, Search, Users, X } from "lucide-react";
import { debounce } from "lodash-es";

import { AppStateContext } from "@/state/AppStateContext";
import { getUsers, inviteGroupParticipant } from "@/api/api";
import { GroupParticipant } from "@/api/models";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface InviteParticipantsModalProps {
  conversationId: string;
  existingParticipants: GroupParticipant[];
  trigger: ReactNode;
  onInvited?: (participants: GroupParticipant[]) => void;
}

const InviteParticipantsModal = ({ conversationId, existingParticipants, trigger, onInvited }: InviteParticipantsModalProps) => {
  const appStateContext = useContext(AppStateContext);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<GroupParticipant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<GroupParticipant[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = appStateContext?.state.currentUser;

  const searchUsers = useCallback(
    debounce(async (term: string) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const res = await getUsers(1, term);
        const data = await res.json();
        const existingIds = new Set(existingParticipants.map((participant) => participant.userId));
        const selectedIds = new Set(selectedParticipants.map((participant) => participant.userId));
        const users: GroupParticipant[] = (data.items || data.value || [])
          .filter((user: any) => user.id !== currentUser?.id)
          .map((user: any) => ({
            userId: user.id,
            name: user.displayName || user.mail || user.id,
            email: user.mail || user.userPrincipalName || "",
          }))
          .filter((user: GroupParticipant) => !existingIds.has(user.userId) && !selectedIds.has(user.userId));

        setSearchResults(users);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400),
    [currentUser, existingParticipants, selectedParticipants]
  );

  const resetState = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedParticipants([]);
    setIsSearching(false);
    setIsSubmitting(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) resetState();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    searchUsers(value);
  };

  const addParticipant = (participant: GroupParticipant) => {
    if (!selectedParticipants.some((selected) => selected.userId === participant.userId)) {
      setSelectedParticipants((prev) => [...prev, participant]);
    }
    setSearchTerm("");
    setSearchResults([]);
  };

  const removeSelectedParticipant = (participantUserId: string) => {
    setSelectedParticipants((prev) => prev.filter((participant) => participant.userId !== participantUserId));
  };

  const handleInvite = async () => {
    if (selectedParticipants.length < 1 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const invitedParticipants: GroupParticipant[] = [];
      for (const participant of selectedParticipants) {
        const success = await inviteGroupParticipant(conversationId, participant);
        if (success) invitedParticipants.push(participant);
      }

      if (invitedParticipants.length > 0) {
        onInvited?.(invitedParticipants);
        handleOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 mb-1">
            <Users size={18} />
            Add People
          </DialogTitle>
          <DialogDescription>
            Add more people directly to this group conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div>
            <label className="text-sm font-medium mb-1 block">Search people</label>
            <div className="flex items-center gap-2 input input-bordered rounded-xl bg-base-100 pr-2">
              <Search size={14} className="text-accent shrink-0" />
              <input
                type="text"
                className="flex-1 bg-transparent outline-none text-sm"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {isSearching && <span className="loading loading-spinner loading-xs" />}
            </div>

            {searchResults.length > 0 && (
              <div className="mt-1 bg-secondary border border-base-300 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map((user) => (
                  <div
                    key={user.userId}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-neutral cursor-pointer text-sm"
                    onClick={() => addParticipant(user)}
                  >
                    <div className="avatar placeholder">
                      <div className="bg-primary text-primary-content rounded-full w-7">
                        <span className="text-xs">{user.name[0]?.toUpperCase()}</span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{user.name}</div>
                      {user.email && <div className="text-xs text-accent truncate">{user.email}</div>}
                    </div>
                    <Plus size={14} className="ml-auto text-primary shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedParticipants.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedParticipants.map((participant) => (
                <div key={participant.userId} className="badge badge-outline gap-1 py-3 px-2 rounded-xl">
                  <span className="text-xs">{participant.name}</span>
                  <button type="button" onClick={() => removeSelectedParticipant(participant.userId)} className="hover:text-error">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button type="button" className="btn btn-ghost" onClick={() => handleOpenChange(false)}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selectedParticipants.length < 1 || isSubmitting}
            onClick={handleInvite}
          >
            {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : <Users size={16} />}
            Add People
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteParticipantsModal;
